import React, {useEffect} from "react";
import Grid from "@mui/material/Grid";
import Plot from "react-plotly.js";
import Typography from "@mui/material/Typography";
import {DataGrid, GridToolbar} from "@mui/x-data-grid";
import {useTheme} from "@mui/material/styles";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {reactMUILocale} from "../i18n";
import {UserContext} from "../App";
import {fetchData} from "../utils/api";
import {makeStringReadable, TagsCell} from "../utils/table-utils";

export default function Runs() {
    const theme = useTheme();
    // init internationalization
    const {t} = useTranslation();

    // get info about current dataset and experiment
    const {
        datasets,
        currentDatasetIdx,
        experiments,
        currentExperimentIdx,
        currentExperimentProgress,
    } = React.useContext(UserContext);

    const [rowSelectionModel, setRowSelectionModel] = React.useState([]);

    const [runsData, setRunsData] = React.useState([
        {experimentType: "Regression"},
    ]);
    const [columnInfo, setColumnInfo] = React.useState([]);
    const [boxPlotDataVal, setBoxPlotDataVal] = React.useState({});
    const [boxPlotDataTrain, setBoxPlotDataTrain] = React.useState({});
    const [barPlotDataVal, setBarPlotDataVal] = React.useState({});
    const [barPlotDataTrain, setBarPlotDataTrain] = React.useState({});

    const fetchRuns = () => {
        fetchData(
            `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}/experiment/${experiments[currentExperimentIdx].experimentName}/runs?include_results=True`
        ).then((data) => {
            // unwrap children dictionaries e.g. metrics and params and change key-val order
            const unwrappedSortedData = data.runs.reduce((acc, run) => {
                const transformedChildren = run.children.map(({ runName, stepName, predictionDetails, metrics, params}) => ({
                    runName,
                    stepName,
                    predictionDetails,
                    ...metrics,
                    ...params
                }));
                return acc.concat(transformedChildren);
            }, []);

            setRunsData(unwrappedSortedData);
            // get column names, using the first entry
            const columnNames = Object.keys(unwrappedSortedData.at(0));
            // remove unnecessary columns
            columnNames.splice(columnNames.indexOf("SHAPBeeswarmPlot"), 1);
            columnNames.splice(columnNames.indexOf("FeatureImportance"), 1);
            columnNames.splice(columnNames.indexOf("AfsDetails"), 1);
            // create column setting dictionary (needed for React MUI Data Grids)
            const columnInfoList = [];
            columnNames.reduce((obj, name) => {
                columnInfoList.push({
                    // set readable header name
                    headerName: makeStringReadable(name),
                    field: name,
                    width: 150,
                    // render Tag and category cells different to other cells
                    renderCell: ["tags", "model_type", "status"].includes(name)
                        ? TagsCell
                        : React.ReactNode,
                });
                return obj;
            }, {});
            setColumnInfo(columnInfoList);
            // as default, set all rows selected - this is done using all row indexes
            setRowSelectionModel([...Array(unwrappedSortedData.length).keys()]);
        });
    };

    // fetch runs data by API requests, when the current experiment is gathered
    useEffect(() => {
        if (datasets.length >= 1) {
            fetchRuns();
        }
    }, [experiments, currentExperimentIdx]);

    // update Plots everytime, the run selection changes
    useEffect(() => {
        if (runsData.at(0).experimentType === "Regression" || runsData.at(0).experimentType === "Forecast") {
            // Box Plot Settings
            const dataVal = rowSelectionModel.map((rowIndex) => ({
                type: "box",
                y: runsData[rowIndex].predictionDetails.AEListVal,
                name: `${runsData.at(rowIndex).runName}_${runsData.at(rowIndex).stepName}`,
                boxpoints: "all",
                marker: {
                    size: 3,
                },
            }));
            setBoxPlotDataVal(dataVal);

            const dataTrain = rowSelectionModel.map((rowIndex) => ({
                type: "box",
                y: runsData[rowIndex].predictionDetails.AEListTrain,
                name: `${runsData.at(rowIndex).runName}_${runsData.at(rowIndex).stepName}`,
                boxpoints: "all",
                marker: {
                    size: 3,
                },
            }));
            setBoxPlotDataTrain(dataTrain);
        }

        //  Bar Plot Data
        if (runsData.at(0).experimentType === "Classification") {
            const {classNames} = runsData[0].predictionDetails;

            const dataVal = rowSelectionModel.map((rowIndex) => ({
                x: classNames,
                y: classNames.map((className, colIndex) => runsData[rowIndex].predictionDetails.confusionMatrixVal[colIndex][colIndex]),
                name: `${runsData.at(rowIndex).runName}_${runsData.at(rowIndex).stepName}`,
                type: "bar",
            }));
            setBarPlotDataVal(dataVal);

            const dataTrain = rowSelectionModel.map((rowIndex) => ({
                x: classNames,
                y: classNames.map((className, colIndex) => runsData[rowIndex].predictionDetails.confusionMatrixTrain[colIndex][colIndex]),
                name: runsData[rowIndex].runName,
                type: "bar",
            }));
            setBarPlotDataTrain(dataTrain);
        }
    }, [rowSelectionModel, runsData]);

    const layoutBoxVali = {
        title: t("runs.AEValPlotTitle"),
        margin: {
            l: 40,
            r: 30,
            b: 80,
            t: 100,
        },
        showlegend: false,
    };
    const layoutBoxTrain = {
        title: t("runs.AETrainPlotTitle"),
        margin: {
            l: 40,
            r: 30,
            b: 80,
            t: 100,
        },
        showlegend: false,
    };

    const layoutBarVali = {
        title: t("runs.AccuracyValPlotTitle"),
        margin: {
            l: 80,
            r: 30,
            b: 80,
            t: 100,
        },
        yaxis: {
            title: t("runs.AccuracyPlotYLabel"),
        },
        xaxis: {
            title: t("runs.AccuracyPlotXLabel"),
        },
        showlegend: true,
        barmode: "group",
        bargap: 0.15,
        bargroupgap: 0.1,
    };

    const layoutBarTrain = {
        title: t("runs.AccuracyTrainPlotTitle"),
        margin: {
            l: 80,
            r: 30,
            b: 80,
            t: 100,
        },
        yaxis: {
            title: t("runs.AccuracyPlotYLabel"),
        },
        xaxis: {
            title: t("runs.AccuracyPlotXLabel"),
        },
        showlegend: true,
        barmode: "group",
        bargap: 0.15,
        bargroupgap: 0.1,
    };

    return (
        <Grid
            id="overview-container"
            container
        >
            <Grid
                container
                sx={{
                    padding: theme.spacing(8, 8),
                    borderRadius: '16px',
                    backgroundColor: 'white',
                }}
            >
                <Grid item xs={12} sx={{marginBottom: theme.spacing(4)}}>
                    <Typography variant="h4" gutterBottom>
                        {t("runs.PageTitle")}
                    </Typography>
                </Grid>
                {
                    // only render if current experiment has fullfilled all required steps and stats are loaded
                    currentExperimentProgress?.trained >= 1 ? (
                        <>
                            <Grid item lg={5} xs={12}>
                                <DataGrid
                                    rows={runsData}
                                    columns={columnInfo}
                                    pageSize={9}
                                    localeText={reactMUILocale}
                                    rowsPerPageOptions={[5]}
                                    checkboxSelection
                                    onRowSelectionModelChange={(newRowSelectionModel) => {
                                        setRowSelectionModel(newRowSelectionModel);
                                    }}
                                    rowSelectionModel={rowSelectionModel}
                                    // row id = index
                                    getRowId={(row) => runsData.indexOf(row)}
                                    components={{
                                        Toolbar: GridToolbar,
                                    }}
                                    initialState={{
                                        columns: {
                                            columnVisibilityModel: {
                                                // Hide columns status and traderName, the other columns will remain visible
                                                status: false,
                                                startTime: false,
                                                endTime: false,
                                                artifactURI: false,
                                                experimentId: false,
                                                runID: false,
                                                predictionDetails: false,
                                                targetMin: false,
                                                targetMax: false,
                                                tags: false,
                                            },
                                        },
                                    }}
                                    autoHeight
                                />
                            </Grid>
                            {runsData.at(0).experimentType === "Classification" ? (
                                <>
                                    <Grid item lg={1} xs={0}/>
                                    <Grid item xs={6}>
                                        <Typography variant="h5" gutterBottom>
                                            {t("runs.Results")}
                                        </Typography>
                                        <Plot
                                            data={Array.from(barPlotDataVal)}
                                            layout={layoutBarVali}
                                        />
                                    </Grid>
                                    <Grid item xs={6}/>
                                    <Grid item xs={5}>
                                        <Plot
                                            data={Array.from(barPlotDataTrain)}
                                            layout={layoutBarTrain}
                                        />
                                    </Grid>
                                </>
                            ) : (
                                <>
                                    <Grid item lg={1} xs={0}/>
                                    <Grid item xs={6}>
                                        <Typography variant="h5" gutterBottom>
                                            {t("runs.Results")}
                                        </Typography>
                                        <Plot
                                            data={Array.from(boxPlotDataVal)}
                                            layout={layoutBoxVali}
                                        />

                                        <Plot
                                            data={Array.from(boxPlotDataTrain)}
                                            layout={layoutBoxTrain}
                                        />
                                    </Grid>
                                </>
                            )}
                        </>
                    ) : (
                        // if user lands on this page, without having all required steps fulfilled, show hint
                        <Grid item xs={12}>
                            {" "}
                            <Typography variant="body">
                                {t("runs.NoExperiments")}
                                <Link to="/train">{t("runs.NoExperiments_LinkText")}</Link>
                                {t("runs.NoExperiments_AfterLink")}
                            </Typography>
                        </Grid>
                    )
                }
            </Grid>
        </Grid>
    );
}
